import React, { useEffect, useMemo, useState } from 'react';
import api, { getApiErrorMessage } from '../api';
import './AdminCompanyModal.css';

const TIER_NAMES = { free: 'Бесплатный', advanced: 'Расширенный', corporate: 'Корпоративный' };

const toDateInput = (iso) => (iso ? new Date(iso).toISOString().split('T')[0] : '');
const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('ru-RU') : '');

// Какой тариф вернётся человеку после выхода из компании
function personalTierText(teacher) {
    const tier = teacher?.personal_tier;
    if (!tier) return '—';
    const name = TIER_NAMES[tier] || tier;
    if (tier === 'free') return name;
    if (!teacher.personal_expires_at) return `${name}, бессрочно`;
    const expired = new Date(teacher.personal_expires_at) < new Date();
    return expired ? `${name} (истёк)` : `${name} до ${formatDate(teacher.personal_expires_at)}`;
}

// Буфер обмена может быть запрещён (настройки браузера, не HTTPS) — тогда копируем старым способом
const copyText = async (text) => {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        // переходим к запасному способу
    }
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try {
        ok = document.execCommand('copy');
    } catch {
        ok = false;
    }
    document.body.removeChild(area);
    return ok;
};

/**
 * Окно компании в админ-панели: настройки (название, статус, срок), участники,
 * добавление существующего или нового аккаунта, удаление компании.
 */
function AdminCompanyModal({ companyId, users, onClose, onChanged }) {
    const [company, setCompany] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(false);

    const [form, setForm] = useState({ name: '', status: 'active', expires: '', unlimited: true });
    const [addMode, setAddMode] = useState('existing');
    const [query, setQuery] = useState('');
    const [newMember, setNewMember] = useState({ email: '', firstName: '', sendEmail: true });
    const [invite, setInvite] = useState(null);
    const [copied, setCopied] = useState(false);

    const load = async () => {
        try {
            const res = await api.get(`/admin/companies/${companyId}`);
            setCompany(res.data);
            setForm({
                name: res.data.name,
                status: res.data.status || 'active',
                expires: toDateInput(res.data.subscription_expires_at),
                unlimited: !res.data.subscription_expires_at,
            });
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось загрузить компанию'));
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [busy, onClose]);

    const run = async (action, successText) => {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            const result = await action();
            await load();
            onChanged();
            if (successText) setNotice(typeof successText === 'function' ? successText(result) : successText);
            return result;
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось выполнить действие'));
            return null;
        } finally {
            setBusy(false);
        }
    };

    const saveSettings = () => {
        if (!form.name.trim()) {
            setError('Введите название компании');
            return;
        }
        if (!form.unlimited && !form.expires) {
            setError('Укажите дату окончания подписки или отметьте «Бессрочно»');
            return;
        }
        run(() => api.patch(`/admin/companies/${companyId}`, {
            name: form.name.trim(),
            status: form.status,
            subscription_expires_at: form.unlimited ? null : new Date(`${form.expires}T23:59:59`).toISOString(),
        }), 'Настройки компании сохранены. Тарифы участников пересчитаны.');
    };

    const memberIds = useMemo(() => new Set((company?.members || []).map((m) => m.teacher_id)), [company]);

    const candidates = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return [];
        return (users || [])
            .filter((u) => u.status === 'active' && !memberIds.has(u.id))
            .filter((u) => [u.email, u.first_name, u.username, String(u.id)]
                .some((v) => v && String(v).toLowerCase().includes(q)))
            .slice(0, 8);
    }, [users, query, memberIds]);

    const addExisting = (user) => run(
        () => api.post(`/admin/companies/${companyId}/members/existing`, { teacher_id: user.id }),
        `${user.email || user.first_name || `ID ${user.id}`} добавлен(а) в компанию.`,
    ).then((res) => { if (res) setQuery(''); });

    const createMember = async () => {
        const email = newMember.email.trim();
        if (!email || !email.includes('@')) {
            setError('Введите email нового сотрудника');
            return;
        }
        setInvite(null);
        setCopied(false);
        const res = await run(() => api.post(`/admin/companies/${companyId}/members/new`, {
            email,
            first_name: newMember.firstName.trim() || null,
            send_reset_link: true,
            send_email: newMember.sendEmail,
        }));
        if (res) {
            setInvite({ email, link: res.data.reset_link, emailSent: res.data.email_sent, wantedEmail: newMember.sendEmail });
            setNewMember({ email: '', firstName: '', sendEmail: newMember.sendEmail });
        }
    };

    const removeMember = (member) => {
        const who = member.teacher?.email || member.teacher?.first_name || `ID ${member.teacher_id}`;
        const back = personalTierText(member.teacher);
        if (!window.confirm(`Убрать ${who} из компании?\n\nКорпоративный тариф от компании перестанет действовать. Вернётся личный тариф: ${back === '—' ? 'Бесплатный' : back}.`)) return;
        run(() => api.delete(`/admin/companies/${companyId}/members/${member.teacher_id}`), `${who} убран(а) из компании.`);
    };

    const deleteCompany = async () => {
        const count = company?.members?.length || 0;
        if (!window.confirm(`Удалить компанию «${company.name}»?\n\nУчастников: ${count}. Им вернутся личные тарифы. Аккаунты и опросы участников не удаляются.`)) return;
        setBusy(true);
        try {
            await api.delete(`/admin/companies/${companyId}`);
            onChanged();
            onClose();
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось удалить компанию'));
            setBusy(false);
        }
    };

    const isActiveNow = company && company.status === 'active'
        && (!company.subscription_expires_at || new Date(company.subscription_expires_at) > new Date());

    return (
        <div className="admin-modal-overlay" role="presentation" onClick={() => !busy && onClose()}>
            <div className="admin-modal admin-modal--wide company-modal" role="dialog" aria-modal="true" aria-labelledby="company-modal-title" onClick={(e) => e.stopPropagation()}>
                <div className="admin-modal__body">
                    {!company ? (
                        <p>{error || 'Загрузка…'}</p>
                    ) : (
                        <>
                            <div className="company-modal__head">
                                <h3 id="company-modal-title">{company.name}</h3>
                                <span className={`status-mock status-mock--${isActiveNow ? 'active' : 'blocked'}`}>
                                    {isActiveNow ? 'Тариф действует' : company.status === 'active' ? 'Срок истёк' : 'Приостановлена'}
                                </span>
                            </div>
                            <p className="company-modal__lead">
                                Пока компания активна и срок не истёк, все её участники получают тариф «Корпоративный».
                                Личный тариф участника (оплаченный или выданный вручную) сохраняется и вернётся, когда человек выйдет из компании или срок закончится.
                            </p>

                            {error && <div className="admin-notice admin-notice--error" role="alert">{error}</div>}
                            {notice && <div className="admin-notice admin-notice--success" role="status">{notice}</div>}

                            <section className="company-modal__section">
                                <h4>Настройки</h4>
                                <div className="company-modal__grid">
                                    <label className="company-field">
                                        <span>Название</span>
                                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                                    </label>
                                    <label className="company-field">
                                        <span>Статус</span>
                                        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                                            <option value="active">Активна</option>
                                            <option value="suspended">Приостановлена (тариф не действует)</option>
                                        </select>
                                    </label>
                                    <div className="company-field">
                                        <span>Корпоративный тариф до</span>
                                        <div className="company-field__row">
                                            <input
                                                type="date"
                                                value={form.expires}
                                                disabled={form.unlimited}
                                                onChange={(e) => setForm({ ...form, expires: e.target.value })}
                                            />
                                            <label className="company-check">
                                                <input
                                                    type="checkbox"
                                                    checked={form.unlimited}
                                                    onChange={(e) => setForm({ ...form, unlimited: e.target.checked })}
                                                />
                                                Бессрочно
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <button type="button" className="btn btn--success" onClick={saveSettings} disabled={busy}>
                                    Сохранить настройки
                                </button>
                            </section>

                            <section className="company-modal__section">
                                <h4>Участники ({company.members?.length || 0})</h4>
                                {company.members?.length ? (
                                    <div className="company-members-table-wrap">
                                        <table className="admin-table company-members-table">
                                            <thead>
                                                <tr>
                                                    <th>Имя</th>
                                                    <th>Email</th>
                                                    <th title="Вернётся после выхода из компании">Личный тариф</th>
                                                    <th aria-label="Действие" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {company.members.map((member) => (
                                                    <tr key={member.id}>
                                                        <td>{member.teacher?.first_name || member.teacher?.username || '—'}</td>
                                                        <td>{member.teacher?.email || '—'}</td>
                                                        <td>{personalTierText(member.teacher)}</td>
                                                        <td>
                                                            <button type="button" className="btn btn--secondary company-remove-btn" onClick={() => removeMember(member)} disabled={busy}>
                                                                Убрать
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="admin-modal-empty">В компании пока нет участников. Добавьте их ниже.</p>
                                )}
                            </section>

                            <section className="company-modal__section">
                                <h4>Добавить участника</h4>
                                <div className="company-tabs" role="tablist">
                                    <button type="button" role="tab" aria-selected={addMode === 'existing'} className={addMode === 'existing' ? 'is-active' : ''} onClick={() => setAddMode('existing')}>
                                        Уже зарегистрирован в СВОД
                                    </button>
                                    <button type="button" role="tab" aria-selected={addMode === 'new'} className={addMode === 'new' ? 'is-active' : ''} onClick={() => setAddMode('new')}>
                                        Новый человек — создать аккаунт
                                    </button>
                                </div>

                                {addMode === 'existing' ? (
                                    <div className="company-add">
                                        <p className="company-hint">Найдите аккаунт по email, имени или ID. Его опросы сохранятся, а тариф станет «Корпоративный».</p>
                                        <input
                                            type="search"
                                            className="company-search"
                                            placeholder="Например, ivanov@mail.ru"
                                            value={query}
                                            onChange={(e) => setQuery(e.target.value)}
                                        />
                                        {query.trim() && (
                                            candidates.length ? (
                                                <ul className="company-candidates">
                                                    {candidates.map((u) => (
                                                        <li key={u.id}>
                                                            <span>
                                                                <b>{u.email || u.username || `ID ${u.id}`}</b>
                                                                <small>{[u.first_name, `ID ${u.id}`, TIER_NAMES[u.subscription_tier] || u.subscription_tier].filter(Boolean).join(' · ')}{u.company_name ? ` · уже в «${u.company_name}»` : ''}</small>
                                                            </span>
                                                            <button type="button" className="btn btn--primary" onClick={() => addExisting(u)} disabled={busy}>Добавить</button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="company-hint">Никого не нашли. Если у человека ещё нет аккаунта — откройте вкладку «Новый человек».</p>
                                            )
                                        )}
                                    </div>
                                ) : (
                                    <div className="company-add">
                                        <p className="company-hint">Создадим аккаунт с тарифом «Корпоративный». Человек сам задаст пароль по ссылке (действует 24 часа).</p>
                                        <div className="company-modal__grid">
                                            <label className="company-field">
                                                <span>Email *</span>
                                                <input type="email" value={newMember.email} onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} placeholder="name@company.ru" />
                                            </label>
                                            <label className="company-field">
                                                <span>Имя</span>
                                                <input value={newMember.firstName} onChange={(e) => setNewMember({ ...newMember, firstName: e.target.value })} placeholder="Необязательно" />
                                            </label>
                                        </div>
                                        <label className="company-check">
                                            <input type="checkbox" checked={newMember.sendEmail} onChange={(e) => setNewMember({ ...newMember, sendEmail: e.target.checked })} />
                                            Отправить приглашение со ссылкой на почту
                                        </label>
                                        <button type="button" className="btn btn--success" onClick={createMember} disabled={busy}>
                                            Создать аккаунт и добавить
                                        </button>
                                        {invite && (
                                            <div className="company-invite" role="status">
                                                <p>
                                                    {invite.emailSent
                                                        ? <>Аккаунт создан, приглашение отправлено на <b>{invite.email}</b>. Ссылка ниже — на случай, если письмо не дойдёт.</>
                                                        : invite.wantedEmail
                                                            ? <>Аккаунт создан, но письмо на <b>{invite.email}</b> отправить не удалось. Передайте ссылку вручную.</>
                                                            : <>Аккаунт создан. Передайте ссылку для установки пароля сотруднику <b>{invite.email}</b>.</>}
                                                </p>
                                                {invite.link && (
                                                    <div className="company-invite__row">
                                                        <input readOnly value={invite.link} onFocus={(e) => e.target.select()} />
                                                        <button type="button" className="btn btn--secondary" onClick={async () => setCopied(await copyText(invite.link) ? 'ok' : 'fail')}>
                                                            {copied === 'ok' ? 'Скопировано ✓' : 'Копировать'}
                                                        </button>
                                                    </div>
                                                )}
                                                {copied === 'fail' && <p className="company-hint">Браузер не дал скопировать — выделите ссылку и нажмите Ctrl+C.</p>}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </section>

                            <section className="company-modal__section company-modal__danger">
                                <div>
                                    <b>Удалить компанию</b>
                                    <p>Участникам вернутся личные тарифы. Аккаунты и опросы не удаляются.</p>
                                </div>
                                <button type="button" className="btn btn--danger" onClick={deleteCompany} disabled={busy}>Удалить компанию</button>
                            </section>
                        </>
                    )}
                </div>
                <div className="admin-modal__footer">
                    <button type="button" className="btn btn--secondary" onClick={onClose} disabled={busy}>Закрыть</button>
                </div>
            </div>
        </div>
    );
}

export default AdminCompanyModal;
